import React, { Component } from "react";

interface ImageUploaderState {
  selectedImage: string | null;
  isDragging: boolean;
}

interface ImageUploaderProps {
  onImageSelect: (imageData: string) => void;
  disabled?: boolean;
}

class ImageUploader extends Component<ImageUploaderProps, ImageUploaderState> {
  private fileInputRef: React.RefObject<HTMLInputElement>;

  constructor(props: ImageUploaderProps) {
    super(props);
    this.state = {
      selectedImage: null,
      isDragging: false,
    };
    this.fileInputRef = React.createRef();
    this.handleFileSelect = this.handleFileSelect.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.handleDragOver = this.handleDragOver.bind(this);
    this.handleDragLeave = this.handleDragLeave.bind(this);
    this.removeImage = this.removeImage.bind(this);
  }

  handleFileSelect(event: React.ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    if (file) {
      this.processFile(file);
    }
  }

  handleDrop(event: React.DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    this.setState({ isDragging: false });

    const file = event.dataTransfer.files?.[0];
    if (file) {
      this.processFile(file);
    }
  }

  handleDragOver(event: React.DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    this.setState({ isDragging: true });
  }

  handleDragLeave(event: React.DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    this.setState({ isDragging: false });
  }

  processFile(file: File): void {
    // Check if file is an image
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file.");
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image size should be less than 5MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageData = e.target?.result as string;
      this.setState({ selectedImage: imageData });
      this.props.onImageSelect(imageData);
    };
    reader.readAsDataURL(file);
  }

  removeImage(): void {
    this.setState({ selectedImage: null });
    this.props.onImageSelect("");
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = "";
    }
  }

  render() {
    const { selectedImage, isDragging } = this.state;
    const { disabled = false } = this.props;

    return (
      <div className="w-full">
        <input
          ref={this.fileInputRef}
          type="file"
          accept="image/*"
          onChange={this.handleFileSelect}
          className="hidden"
          disabled={disabled}
        />

        {selectedImage ? (
          <div className="relative">
            <div className="w-full h-48 rounded-lg overflow-hidden border-2 border-gray-200">
              <img
                src={selectedImage}
                alt="Selected"
                className="w-full h-full object-cover"
              />
            </div>
            <button
              type="button"
              onClick={this.removeImage}
              disabled={disabled}
              className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Remove image"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        ) : (
          <div
            className={`w-full h-32 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition duration-200 ${
              isDragging
                ? "border-blue-400 bg-blue-50"
                : "border-gray-300 hover:border-gray-400"
            } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            onClick={() => !disabled && this.fileInputRef.current?.click()}
            onDrop={this.handleDrop}
            onDragOver={this.handleDragOver}
            onDragLeave={this.handleDragLeave}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-gray-400 mb-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p className="text-sm text-gray-500 text-center">
              Click to upload or drag and drop
              <br />
              <span className="text-xs">PNG, JPG up to 5MB</span>
            </p>
          </div>
        )}
      </div>
    );
  }
}

export default ImageUploader;
